/**
 * Смена стадии с мгновенным откликом и откатом.
 *   1. стадия в карточке меняется сразу (человек видит результат);
 *   2. уходит запрос — через Chatwoot (он сперва спрашивает CRM) или напрямую в CRM;
 *   3. CRM может отказать (правила перехода, «нужна причина» и т. д.) — тогда карточка возвращается как была,
 *      а причина показывается её же словами.
 */
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type { CardResponse, CardStage } from '@/api/crmCard';
import type { StageRoute } from './dealCard';

export interface StageChangeInput {
  qc: QueryClient;
  cardKey: QueryKey;
  stage: CardStage;
  route: StageRoute;
  dealId: string;
  /** Стадия записана у переписки (кэш переписки и списков). */
  onApplied?: (stage: CardStage) => void;
  /** Запросы (в тесте подменяются). */
  viaChatwoot: (stageId: string) => Promise<unknown>;
  viaCrm: (dealId: string, stageId: string) => Promise<unknown>;
}

export type StageChangeResult = { ok: true } | { ok: false; error: string };

/** Карточка с другой стадией сделки. */
export function withStage(card: CardResponse, stage: CardStage): CardResponse {
  if (!card.deal) return card;
  return { ...card, deal: { ...card.deal, stageId: stage.id, stage } };
}

export async function changeStage(i: StageChangeInput): Promise<StageChangeResult> {
  const before = i.qc.getQueryData<CardResponse>(i.cardKey);
  if (before) i.qc.setQueryData<CardResponse>(i.cardKey, withStage(before, i.stage));
  try {
    if (i.route === 'chatwoot') await i.viaChatwoot(i.stage.id);
    else await i.viaCrm(i.dealId, i.stage.id);
    i.onApplied?.(i.stage);
    // Остальное в карточке (сумма, ответственный, правила) могло измениться — перечитаем в фоне.
    void i.qc.invalidateQueries({ queryKey: i.cardKey });
    return { ok: true };
  } catch (e) {
    // Откат: возвращаем ровно то, что было до нашего изменения.
    if (before) i.qc.setQueryData<CardResponse>(i.cardKey, before);
    return { ok: false, error: e instanceof Error && e.message ? e.message : 'CRM не приняла изменение' };
  }
}
